import { nextTick, onUnmounted, ref, watch } from 'vue';
import { debounce } from 'lodash-es';

import { useWindowSize } from '@/composables/useWindowSize';

import { BUTTON_SIZE, DEFAULT_POSITION_X, DRAG_THRESHOLD, EDGE_MARGIN } from './constants';

export interface IPosition {
  x: number;
  y: number;
}

const STORAGE_KEY = 'support_icon_position';

const savePosition = debounce((pos: IPosition) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
}, 300);

export function useSupportButton(onClick?: () => void) {
  const position = ref<IPosition>({ x: DEFAULT_POSITION_X, y: 0 });
  const isDragging = ref(false);
  const isReady = ref(false);
  const windowSize = useWindowSize();
  let initPositionUnwatch: (() => void) | null = null;

  const clampPosition = (x: number, y: number): IPosition => {
    const maxX = windowSize.value.width - BUTTON_SIZE - EDGE_MARGIN;
    const maxY = windowSize.value.height - BUTTON_SIZE - EDGE_MARGIN;
    return {
      x: Math.max(EDGE_MARGIN, Math.min(x, maxX)),
      y: Math.max(EDGE_MARGIN, Math.min(y, maxY))
    };
  };

  const onMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    isDragging.value = true;
    let hasActuallyDragged = false;
    const startX = e.clientX;
    const startY = e.clientY;
    const startPosition = { ...position.value };
    let rafId: number | null = null;
    let pendingPosition: IPosition | null = null;

    const updatePosition = () => {
      if (pendingPosition) {
        position.value = pendingPosition;
        pendingPosition = null;
      }
      rafId = null;
    };

    const onMouseMove: EventListener = (event) => {
      const moveEvent = event as MouseEvent;
      moveEvent.preventDefault();

      if (!hasActuallyDragged) {
        const deltaX = Math.abs(moveEvent.clientX - startX);
        const deltaY = Math.abs(moveEvent.clientY - startY);
        if (deltaX < DRAG_THRESHOLD && deltaY < DRAG_THRESHOLD) return;
        hasActuallyDragged = true;
      }

      const newRight = startPosition.x + (startX - moveEvent.clientX);
      const newBottom = startPosition.y + (startY - moveEvent.clientY);

      pendingPosition = clampPosition(newRight, newBottom);
      if (!rafId) rafId = requestAnimationFrame(updatePosition);
    };

    const onMouseUp = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (hasActuallyDragged) {
        savePosition(position.value);
      } else {
        onClick?.();
      }
      isDragging.value = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    document.addEventListener('mousemove', onMouseMove, { passive: false });
    document.addEventListener('mouseup', onMouseUp, { once: true });
  };

  const initPosition = () => {
    initPositionUnwatch?.();
    const savedPosition = localStorage.getItem(STORAGE_KEY);
    if (savedPosition) {
      try {
        position.value = JSON.parse(savedPosition);
      } catch (e) {
        console.error('Failed to parse saved position:', e);
      }
    } else {
      const initY = Math.max(EDGE_MARGIN, window.innerHeight * 0.25 - BUTTON_SIZE);
      position.value = { x: DEFAULT_POSITION_X, y: initY };
    }
    nextTick().then(() => {
      const checkReady = () => {
        if (windowSize.value?.width > 0 && windowSize.value?.height > 0) {
          isReady.value = true;
          return true;
        }
        return false;
      };

      if (!checkReady()) {
        initPositionUnwatch = watch(
          () => windowSize.value,
          () => {
            if (checkReady()) {
              initPositionUnwatch?.();
              initPositionUnwatch = null;
            }
          }
        );
      }
    });
  };

  onUnmounted(() => {
    initPositionUnwatch?.();
    initPositionUnwatch = null;
  });

  watch(
    () => windowSize.value,
    () => {
      const clamped = clampPosition(position.value.x, position.value.y);
      if (clamped.x !== position.value.x || clamped.y !== position.value.y) {
        position.value = clamped;
        savePosition(position.value);
      }
    }
  );

  return {
    position,
    isDragging,
    isReady,
    onMouseDown,
    initPosition
  };
}
